import {Grid, makeStyles, Typography} from '@material-ui/core';
import {observer} from 'mobx-react-lite';
import {useEffect} from 'react';
import {Approval} from '../../Core/mobx/ParameterStore/Approval';
import {Parameter} from '../../Core/mobx/ParameterStore/ParameterStore';
import {Prequal} from '../../Core/mobx/ParameterStore/Prequal';
import {fieldOptions} from '../../Core/mobx/ParameterStore/PrequalXref';
import RootStore from '../../Core/mobx/RootStore';
import {labelTextStyle, TextBox} from '../../Core/UI';
import {SelectInputElement} from '../PreQualification/Components/SelectInputElement';
import DateFnsUtils from '@date-io/date-fns';
import 'date-fns';
import {KeyboardDatePicker, MuiPickersUtilsProvider} from '@material-ui/pickers';

const useStyles = makeStyles({
    row: {
        marginBottom: 24,
    },
    dateOfBirthLabel: {
        marginBottom: 12,
        display: 'inline-flex',
        gap: 6,
    },
    ageLabel: {
        display: 'inline-flex',
        marginBottom: 12,
    },
    usVisaHolder: {
        margin: '0 8px 56px',
        boxShadow: '0px 0px 5px 0px #00000033',
    },
    usVisaHolderLabel: {
        marginBottom: 16,
        fontFamily: 'Fredoka',
        fontSize: 18,
        fontWeight: 500,
        lineHeight: '100%',
    },
    separatedLabel: {
        display: 'inline-flex',
        marginBottom: 12,
    },
});

type BasicProps = {
    valueChanged: (p?: Parameter & {name?: string}, value?: any, name?: string) => void;
    parameters: Approval | Prequal;
    preapproval?: boolean;
    quotes?: boolean;
    threeColumnLayout?: boolean;
    prequal?: boolean;
};

const Basic = observer(({parameters, valueChanged, preapproval, quotes, threeColumnLayout, prequal}: BasicProps) => {
    const {parameterStore} = RootStore.agentPortalStore || {};
    const {onValueChanged, quote: quoteParameters} = parameterStore || {};
    const required = (
        <span>
            <span style={{color: 'red'}}>*</span>
        </span>
    );

    const classes = useStyles();

    useEffect(() => {
        const {byear, bmonth, bday} = parameters || {};
        if (!byear?.value || !bmonth?.value || !bday?.value) return;

        const bd = new Date(byear?.value, bmonth?.value - 1, bday?.value);
        const cd = new Date();

        const yd = cd.getUTCFullYear() - bd.getUTCFullYear();
        const md = cd.getUTCMonth() - bd.getUTCMonth();
        const dd = cd.getUTCDate() - bd.getUTCDate();
        const age = Math.floor(yd + md / 12 + dd / 30 / 12);

        onValueChanged(null, parameters.age, age);

        if (age < 66) {
            onValueChanged(null, quoteParameters.deathBenefitOption, 'Increasing');
            onValueChanged(null, quoteParameters.deathBenefitOptionLSWIUL, 'B_Increasing');
        }
        if (age >= 66) {
            onValueChanged(null, quoteParameters.deathBenefitOption, 'Level');
            onValueChanged(null, quoteParameters.deathBenefitOptionLSWIUL, 'A_Level');
        }
    }, [parameters.byear?.value, parameters.bmonth?.value, parameters.bday?.value, onValueChanged, quoteParameters]);

    const columnNumberAge = threeColumnLayout ? 4 : 3;
    const columnNumber = threeColumnLayout ? 4 : 3;

    const birthDateValue =
        parameters.byear?.value && parameters.bmonth?.value && parameters.bday?.value
            ? new Date(
                  Number(parameters.byear.value),
                  Number(parameters.bmonth.value) - 1,
                  Number(parameters.bday.value),
              )
            : null;

    const minBirthDate = new Date(1935, 0, 1);
    const maxBirthDate = new Date();

    const handleDateChange = (date: Date | null) => {
        if (date && date instanceof Date && !isNaN(date.getTime())) {
            const year = date.getFullYear();
            const month = date.getMonth() + 1;
            const day = date.getDate();

            if (year < 1935 || year > maxBirthDate.getFullYear()) {
                return;
            }
            if (month < 1 || month > 12) {
                return;
            }
            const daysInMonth = new Date(year, month, 0).getDate();
            if (day < 1 || day > daysInMonth) {
                return;
            }
            if (date > maxBirthDate) {
                return;
            }

            valueChanged(parameters?.byear, String(year), 'byear');
            valueChanged(parameters?.bmonth, String(month).padStart(2, '0'), 'bmonth');
            valueChanged(parameters?.bday, String(day).padStart(2, '0'), 'bday');
        }
    };

    return (
        <>
            {prequal ? (
                <>
                    <Grid item xs={4} className={classes.row}>
                        <Typography className={classes.separatedLabel} style={labelTextStyle}>
                            <span>{required}</span>
                            <span>First Name</span>
                        </Typography>
                        <TextBox
                            id={'firstName'}
                            value={parameters.firstName?.value}
                            onChange={(value) => valueChanged(parameters.firstName, value)}
                        />
                    </Grid>
                    <Grid item xs={4} className={classes.row}>
                        <Typography className={classes.separatedLabel} style={labelTextStyle}>
                            <span>{required}</span>
                            <span>Last Name</span>
                        </Typography>
                        <TextBox
                            id={'lastName'}
                            value={parameters.lastName?.value}
                            onChange={(value) => valueChanged(parameters.lastName, value)}
                        />
                    </Grid>
                    <Grid item xs={2} className={classes.row}>
                        <Typography className={classes.separatedLabel} style={labelTextStyle}>
                            <span>Middle Initial</span>
                        </Typography>
                        <TextBox
                            id={'middleName'}
                            value={parameters.middleName?.value}
                            onChange={(value) => valueChanged(parameters.middleName, value)}
                        />
                    </Grid>
                    <Grid item xs={2} className={classes.row}>
                        <Typography className={classes.separatedLabel} style={labelTextStyle}>
                            <span>Suffix</span>
                        </Typography>
                        <TextBox
                            id={'suffix'}
                            value={parameters.suffix?.value}
                            onChange={(value) => valueChanged(parameters.suffix, value)}
                        />
                    </Grid>
                    <Grid container item direction="row" xs={4} className={classes.row}>
                        <Grid item xs={9}>
                            <Typography className={classes.dateOfBirthLabel} style={labelTextStyle}>
                                <span>{required}</span>
                                <span>Date Of Birth</span>
                            </Typography>
                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                <KeyboardDatePicker
                                    inputVariant="outlined"
                                    variant="inline"
                                    openTo="year"
                                    views={['year', 'month', 'date']}
                                    format="MM/dd/yyyy"
                                    placeholder="MM/dd/yyyy"
                                    style={{
                                        width: '100%',
                                    }}
                                    InputProps={{
                                        style: {
                                            height: 48,
                                            borderTopRightRadius: 0,
                                            borderBottomRightRadius: 0,
                                        },
                                    }}
                                    InputLabelProps={{
                                        shrink: !!birthDateValue,
                                        style: birthDateValue
                                            ? {}
                                            : {
                                                  paddingLeft: 14,
                                                  top: '50%',
                                                  transform: 'translateY(-50%)',
                                              },
                                    }}
                                    value={birthDateValue}
                                    minDate={minBirthDate}
                                    maxDate={maxBirthDate}
                                    onChange={handleDateChange}
                                    disableToolbar
                                    KeyboardButtonProps={{
                                        'aria-label': 'change date',
                                        style: {display: 'none'},
                                    }}
                                />
                            </MuiPickersUtilsProvider>
                        </Grid>
                        <Grid item xs={3}>
                            <Typography className={classes.ageLabel} style={labelTextStyle}>
                                <span>Age</span>
                            </Typography>
                            <TextBox
                                disabled
                                value={parameters.age.value || '  ?'}
                                style={{heigh: 48, marginTop: 0, backgroundColor: '#F4F4F4'}}
                                InputProps={{
                                    style: {
                                        height: 48,
                                        borderTopLeftRadius: 0,
                                        borderBottomLeftRadius: 0,
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>

                    <Grid item xs={columnNumber} className={classes.row}>
                        <SelectInputElement
                            param={{
                                label: 'Gender',
                                options: fieldOptions.gender.options,
                                ...parameters.gender,
                                name: 'gender',
                            }}
                            required={required}
                            valueChanged={valueChanged}
                            newLabelType
                        />
                    </Grid>
                    <Grid item xs={4} className={classes.row}>
                        <SelectInputElement
                            param={{
                                label: 'U.S. Citizenship',
                                name: 'usCitizenship',
                                options: fieldOptions.usCitizenship.options,
                                ...parameters.usCitizenship,
                            }}
                            required={required}
                            valueChanged={valueChanged}
                            newLabelType
                        />
                    </Grid>
                    {parameters.usCitizenship.value === 'USVisaHolder' && (
                        <Grid
                            item
                            xs={12}
                            className={classes.usVisaHolder}
                            style={{
                                padding: '17px 25px 24px',
                            }}
                        >
                            <Grid item xs={4}>
                                <Typography className={classes.usVisaHolderLabel}>US Citizen</Typography>
                                <SelectInputElement
                                    param={{
                                        label: 'Which type of visa?',
                                        name: 'usVisaType',
                                        options: fieldOptions.usVisaType.options,
                                        ...parameters.usVisaType,
                                    }}
                                    required={required}
                                    valueChanged={valueChanged}
                                    newLabelType
                                />
                            </Grid>
                        </Grid>
                    )}
                    {!quotes && (
                        <Grid item xs={preapproval ? 4 : columnNumber}>
                            <SelectInputElement
                                param={{
                                    label: 'Marital status?',
                                    options: fieldOptions.maritalStatus.options,
                                    ...parameters.maritalStatus,
                                    name: 'maritalStatus',
                                }}
                                required={''}
                                valueChanged={valueChanged}
                                newLabelType={threeColumnLayout}
                            />
                        </Grid>
                    )}
                    {!preapproval && !quotes && (
                        <Grid item xs={columnNumber}>
                            <SelectInputElement
                                param={{
                                    label: 'State',
                                    options: fieldOptions.state.options,
                                    ...parameters.state,
                                    name: 'state',
                                }}
                                required={required}
                                valueChanged={valueChanged}
                                newLabelType={threeColumnLayout}
                            />
                        </Grid>
                    )}
                </>
            ) : (
                <>
                    <Grid item xs={preapproval ? 2 : columnNumberAge}>
                        <SelectInputElement
                            param={{
                                label: 'Birth Month',
                                options: fieldOptions.bmonth.options,
                                ...parameters.bmonth,
                                name: 'bmonth',
                            }}
                            required={required}
                            valueChanged={valueChanged}
                            newLabelType={threeColumnLayout}
                        />
                    </Grid>
                    <Grid item xs={preapproval ? 2 : columnNumberAge}>
                        <SelectInputElement
                            param={{
                                label: 'Birth Day',
                                options: fieldOptions.bday.options,
                                ...parameters.bday,
                                name: 'bday',
                            }}
                            required={required}
                            valueChanged={valueChanged}
                            newLabelType={threeColumnLayout}
                        />
                    </Grid>
                    <Grid item xs={preapproval ? 2 : columnNumberAge}>
                        <SelectInputElement
                            param={{
                                label: 'Birth Year',
                                options: fieldOptions.byear.options,
                                ...parameters.byear,
                                name: 'byear',
                            }}
                            required={required}
                            valueChanged={valueChanged}
                            newLabelType={threeColumnLayout}
                        />
                    </Grid>
                    <Grid item xs={preapproval || quotes ? 2 : columnNumberAge}>
                        <TextBox disabled value={parameters.age.value} label="Age" shrink />
                    </Grid>
                    <Grid item xs={preapproval ? 4 : columnNumber}>
                        <SelectInputElement
                            param={{
                                label: 'Gender',
                                options: fieldOptions.gender.options,
                                ...parameters.gender,
                                name: 'gender',
                            }}
                            required={required}
                            valueChanged={valueChanged}
                            newLabelType={threeColumnLayout}
                        />
                    </Grid>
                    {!preapproval && !quotes && (
                        <Grid item xs={columnNumber}>
                            <SelectInputElement
                                param={{
                                    label: 'State',
                                    options: fieldOptions.state.options,
                                    ...parameters.state,
                                    name: 'state',
                                }}
                                required={required}
                                valueChanged={valueChanged}
                                newLabelType={threeColumnLayout}
                            />
                        </Grid>
                    )}
                    {!quotes && (
                        <Grid item xs={preapproval ? 4 : columnNumber}>
                            <SelectInputElement
                                param={{
                                    label: 'Marital status?',
                                    options: fieldOptions.maritalStatus.options,
                                    ...parameters.maritalStatus,
                                    name: 'maritalStatus',
                                }}
                                required={''}
                                valueChanged={valueChanged}
                                newLabelType={threeColumnLayout}
                            />
                        </Grid>
                    )}
                </>
            )}
        </>
    );
});

export default Basic;
